import { useEffect } from 'react'
import { fbt } from 'fbtee'
import { Link } from 'lucide-react'
import { InviteQrDialog } from './InviteQrDialog'
import { Button } from './ui/button'
import { copyLink } from '../lib/share'
import { getTotemShareUrl } from '../lib/utils'
import { track } from '../lib/analytics'
import { inviteLinkCopiedToast, inviteLinkCopyErrorToast } from '../lib/commonStrings'
import type { Totem } from '../types'

// The QR is the product here, not a fallback: it gets printed and taped to the
// physical totem so strangers in the crowd can scan it. Copying the link is the
// secondary path, for sharing in a group chat before anyone leaves the house.
export function TotemShareDialog({
  open,
  onOpenChange,
  groupId,
  totem,
  surface,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  totem: Totem
  surface: 'manage' | 'route_view'
}) {
  const url = getTotemShareUrl(groupId, totem.id)

  // Opening the dialog is the share act — the QR is meant to be screenshotted or
  // printed from here, so there's no later "confirm" moment to measure.
  useEffect(() => {
    if (open) track('totem_share_opened', { group_id: groupId, surface })
  }, [open, groupId, surface])

  return (
    <InviteQrDialog
      open={open}
      onOpenChange={onOpenChange}
      value={url}
      title={
        <fbt desc="Title of the dialog showing a totem's shareable QR code">
          Share <fbt:param name="totemName">{totem.name}</fbt:param>
        </fbt>
      }
      description={fbt(
        'Print this and tape it to your totem. Anyone who scans it follows along and sees where it is heading.',
        "Description of the totem QR share dialog explaining what the code does",
      )}
      footer={
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            copyLink(url, {
              successToast: inviteLinkCopiedToast(),
              errorToast: inviteLinkCopyErrorToast(),
            })
          }
        >
          <Link className="size-4" />
          {fbt('Copy link', 'Button that copies a totem share link to the clipboard')}
        </Button>
      }
    />
  )
}
